import React, { useState, useEffect, useCallback } from 'react';
import { FiTool, FiPlus, FiEdit2, FiX, FiCheck } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none',
};

const EMPTY = { name: '', description: '' };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [editId, setEditId]     = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/vendors/services/manage');
      setServices(data.services || []);
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const openAdd  = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (s) => { setForm({ name: s.name, description: s.description || '' }); setEditId(s.id); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY); };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      setSaving(true);
      if (editId) {
        await API.put(`/vendors/services/manage/${editId}`, form);
        toast.success('Service updated');
      } else {
        await API.post('/vendors/services/manage', form);
        toast.success('Service added');
      }
      closeForm();
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (s) => {
    try {
      await API.put(`/vendors/services/manage/${s.id}`, { status: s.status === 'active' ? 'inactive' : 'active' });
      toast.success('Status updated');
      fetchServices();
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <div style={{ padding: '32px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: '1.4rem', margin: 0 }}>Services</h2>
          <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>
            Master list of services vendors can provide
          </p>
        </div>
        <button className="crm-btn crm-btn--primary" onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiPlus size={15} /> Add Service
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="crm-card" style={{ marginBottom: 24, border: '1.5px solid #6366f1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{editId ? 'Edit Service' : 'New Service'}</span>
            <button onClick={closeForm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><FiX size={18} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Service Name *</label>
              <input
                style={inputStyle}
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Offset Printing"
              />
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <input
                style={inputStyle}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional short description"
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="crm-btn" onClick={closeForm}>Cancel</button>
            <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editId ? 'Update' : 'Add Service'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="crm-card">
        {loading ? (
          <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>
        ) : services.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '48px 0' }}>
            <FiTool size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <p>No services yet. Add one above.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
                {['Service', 'Description', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {services.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px 10px', fontWeight: 600 }}>{s.name}</td>
                  <td style={{ padding: '12px 10px', color: '#6b7280' }}>{s.description || '—'}</td>
                  <td style={{ padding: '12px 10px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                      background: s.status === 'active' ? '#dcfce7' : '#f3f4f6',
                      color:      s.status === 'active' ? '#16a34a' : '#6b7280',
                    }}>{s.status}</span>
                  </td>
                  <td style={{ padding: '12px 10px' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button onClick={() => openEdit(s)} title="Edit" style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer' }}>
                        <FiEdit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(s)}
                        title={s.status === 'active' ? 'Deactivate' : 'Activate'}
                        style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer' }}
                      >
                        {s.status === 'active'
                          ? <FiX size={14} color="#dc2626" />
                          : <FiCheck size={14} color="#16a34a" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
